import { ElementProps } from '@/components/sections/builder/types';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import { ElementDataType } from '@/components/sections/builder/elements/body/multicolumn/type';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { useSearchParams } from 'next/navigation';
import clsx from 'clsx';

export default function ElementContent({ element, preview }: ElementProps) {
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';

  const content = element.data as ElementDataType;

  const childs = element.childs;

  const isExternalLink = content.buttonLink?.includes('http');

  return (
    <>
      <section className={cn('multicolumn_wrapper')}>
        <div className='theme_layout_page_width mx-auto duration-100'>
          <div className='multicolumn-padding relative'>
            <div className='relative overflow-hidden px-2 py-2 lg:px-4 lg:py-8'>
              <div
                className={clsx(
                  'mb-16 flex flex-col',
                  content.titleAlign === 'left' && '[&>*]:text-left',
                  content.titleAlign === 'center' && '[&>*]:text-center',
                  content.titleAlign === 'right' && '[&>*]:text-right'
                )}
              >
                <h2
                  className={clsx(
                    'theme_font_heading multicolumn_heading text-base font-bold tracking-tight',
                    content.headingSize === 'large' &&
                      'text-xl sm:text-3xl md:text-4xl lg:text-5xl',
                    content.headingSize === 'medium' &&
                      'text-xl sm:text-2xl md:text-3xl lg:text-4xl',
                    content.headingSize === 'small' && 'text-lg sm:text-xl md:text-2xl lg:text-3xl'
                  )}
                  style={{
                    color: content.colorScheme?.text || '#52535B',
                  }}
                  dangerouslySetInnerHTML={{ __html: content.heading }}
                />
                {content.description && (
                  <h3
                    className={cn(
                      'minimal-tiptap-editor tiptap ProseMirror font-light',
                      content.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                      content.textStyle === 'subtitle' && 'text-sm md:text-base lg:text-lg',
                      content.textStyle === 'uppercase' &&
                        'text-base uppercase md:text-lg lg:text-xl'
                    )}
                    style={{
                      color: content.colorScheme?.textSecondary || '#52535B',
                    }}
                    data-animation='true'
                    dangerouslySetInnerHTML={{
                      __html: content.description as string,
                    }}
                  />
                )}
              </div>
              {(!content.enableSwipeMobile || content.enableSwipeMobile) && (
                <div
                  className={cn(
                    'theme_layout_space_grid_horizontal theme_layout_space_grid_vertical gap-y-2',
                    content.enableSwipeMobile ? 'hidden md:grid' : 'grid',
                    `sm:grid-cols-${content.numberOfColumns - 1}`,
                    `md:grid-cols-${content.numberOfColumns}`,
                    `lg:grid-cols-${content.numberOfColumns}`,
                    content.totalColMobile === 'two_columns' ? 'grid-cols-2' : 'grid-cols-1'
                  )}
                >
                  {childs?.map((child) => (
                    <div key={child.id} className='mb-0 md:mb-10'>
                      <WrapperChild
                        child={{
                          ...child,
                          parentData: content,
                          themeId: themeId,
                        }}
                        parentType={element.type}
                        preview={preview}
                      />
                    </div>
                  ))}
                </div>
              )}
              <div
                className={clsx(
                  `desktop-grid-col theme_layout_space_grid_horizontal theme_layout_space_grid_vertical  place-content-center md:hidden`,
                  content.enableSwipeMobile ? 'grid' : 'hidden'
                )}
              >
                <Carousel className='max-w-full' opts={{ loop: true }}>
                  <CarouselContent>
                    {childs?.map((child) => (
                      <CarouselItem
                        className={cn(
                          content.totalColMobile === 'two_columns' &&
                            content.enableSwipeMobile &&
                            'basis-1/2'
                        )}
                        key={child.id}
                      >
                        <WrapperChild
                          child={{
                            ...child,
                            parentData: content,
                            themeId: themeId,
                          }}
                          parentType={element.type}
                          preview={preview}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className='left-4 h-12 w-12 [&>svg]:h-6 [&>svg]:w-6' />
                  <CarouselNext className='right-4 h-12 w-12 [&>svg]:h-6 [&>svg]:w-6' />
                </Carousel>
              </div>

              <div className='mt-3 flex justify-center'>
                {content.buttonLabel && (
                  <Link
                    href={
                      isExternalLink
                        ? content.buttonLink || '#'
                        : `/${(content.buttonLink || '').replace(/^\//, '')}${
                            preview ? `?theme_id=${themeId}&isPreview=true} ` : ''
                          }`
                    }
                  >
                    <button
                      className='button_color_scheme_multicolumn items- button_radius_multicolumn flex w-52 justify-center rounded-sm py-3 text-base'
                      type='button'
                    >
                      {content.buttonLabel}
                    </button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <style jsx>
        {`
          .multicolumn_wrapper {
            background-color: ${content.colorScheme?.background || 'white'};
            color: ${content.colorScheme?.text || '#52535B'};
          }
          @media (min-width: 768px) {
            .multicolumn-padding {
              padding-top: ${content.padding?.top || 0}px;
              padding-bottom: ${content.padding?.bottom || 0}px;
              padding-left: ${content.padding?.left || 20}px;
              padding-right: ${content.padding?.right || 20}px;
            }
          }
          .multicolumn_heading {
            font-size: ${content.headingSize}px;
            line-height: ${content.headingSize}px;
            color: ${content.colorScheme?.text || '#52535B'};
          }
        `}
      </style>
      <style jsx global>
        {`
          .button_radius_multicolumn {
            border-radius: ${content.buttonRadius}px;
          }
          .button_color_scheme_multicolumn {
            background-color: ${content.colorScheme?.btnSolidBackgroud} !important;
            color: ${content.colorScheme?.btnSolidlabel};
            box-shadow: 0.5px 0.5px 2px ${content.colorScheme?.shadow};
          }
        `}
      </style>
    </>
  );
}
